/**
 * Carrier MCP — Greenzone whitelist tools (stdio build, CAR-41).
 *
 * Mirrors apps/mcp-server/src/tools-greenzone.ts exactly.
 * In stdio mode, GREENZONE_STATE_KV is stubbed (null) — state operations
 * log a warning but still return the Kapture step sequence so Claude Desktop
 * users can execute the portal automation flow.
 */

import { z } from "zod";
import type { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import type { ToolContext } from "./tools.js";
import {
  readState,
  writeState,
  stateAddHost,
  stateAddIp,
  stateRemoveHost,
  stateRemoveIp,
  buildAddHostSteps,
  buildAddIpSteps,
  buildRemoveHostSteps,
  buildRemoveIpSteps,
  buildListSteps,
  resolvePortalBaseUrl,
  type GreenzoneState,
} from "./greenzone-whitelist.js";
import type { Env } from "./types.js";

// ---------------------------------------------------------------------------
// KV namespace accessor (nullable — graceful degradation in stdio mode)
// ---------------------------------------------------------------------------

function getGreenzoneKv(env: Env): KVNamespace | null {
  return (env as unknown as { GREENZONE_STATE_KV?: KVNamespace }).GREENZONE_STATE_KV ?? null;
}

// ---------------------------------------------------------------------------
// Shared tool result type
// ---------------------------------------------------------------------------

type ToolResult = { content: Array<{ type: "text"; text: string }>; isError?: boolean };

function ok(payload: unknown): ToolResult {
  return { content: [{ type: "text", text: JSON.stringify(payload, null, 2) }] };
}

function err(message: string): ToolResult {
  return { isError: true, content: [{ type: "text", text: message }] };
}

const KAPTURE_UNAVAILABLE_MSG =
  "Execute each Kapture step using mcp__kapture__* tools in sequence. " +
  "Ensure the Kapture extension is active in Chrome before invoking.";

// ---------------------------------------------------------------------------
// Tool registration
// ---------------------------------------------------------------------------

export function registerGreenzoneTools(server: McpServer, ctx: ToolContext): void {
  // =========================================================================
  // greenzone_whitelist_add
  // =========================================================================
  server.registerTool(
    "greenzone_whitelist_add",
    {
      title: "Greenzone Whitelist — Add",
      description:
        "Add a hostname and/or IP address to the Bridge4IP Greenzone whitelist. " +
        "The Greenzone whitelist controls which hosts/IPs are exempt from data charging " +
        "after a subscriber's bundle is depleted. " +
        "This operates via browser automation (Kapture) against the OCS portal — no REST API exists for this surface. " +
        "Provide `host` (e.g. 'example.com') and/or `ip` (e.g. '203.0.113.0/24'). " +
        "At least one of host or ip must be provided. " +
        "Returns Kapture step sequence for Claude Code to execute. " +
        "Requires 'write' scope and Kapture running in Sam's Chrome.",
      inputSchema: {
        host: z
          .string()
          .optional()
          .describe("Hostname to whitelist (e.g. 'example.com')"),
        ip: z
          .string()
          .optional()
          .describe("IP address or CIDR to whitelist (e.g. '203.0.113.0/24')"),
        dry_run: z
          .boolean()
          .optional()
          .describe("If true, return the Kapture steps without executing portal automation"),
      },
      annotations: { destructiveHint: true },
    },
    async (args): Promise<ToolResult> => {
      if (!ctx.props.scope.includes("write")) {
        return err(
          `Scope denied: greenzone_whitelist_add requires 'write' scope. Your token has: [${ctx.props.scope.join(", ")}].`,
        );
      }

      const { host, ip, dry_run } = args as { host?: string; ip?: string; dry_run?: boolean };

      if (!host && !ip) {
        return err("At least one of 'host' or 'ip' must be provided.");
      }

      const portalBaseUrl = resolvePortalBaseUrl(ctx.env);

      const steps = [
        ...(host ? buildAddHostSteps(portalBaseUrl, host) : []),
        ...(ip ? buildAddIpSteps(portalBaseUrl, ip) : []),
      ];

      if (dry_run) {
        return ok({
          dry_run: true,
          message: "Would execute the following Kapture steps against the OCS portal:",
          kapture_steps: steps,
          host,
          ip,
        });
      }

      const kv = getGreenzoneKv(ctx.env);
      let state: GreenzoneState | undefined;
      let kvWarning: string | undefined;

      if (kv) {
        try {
          state = await readState(kv);
          if (host) state = stateAddHost(state, host);
          if (ip) state = stateAddIp(state, ip);
          await writeState(kv, state);
        } catch (e) {
          kvWarning = `KV write failed: ${e instanceof Error ? e.message : String(e)}`;
        }
      } else {
        kvWarning = "GREENZONE_STATE_KV not bound in stdio mode — state not persisted.";
      }

      return ok({
        requires_kapture: true,
        message:
          "Execute the Kapture steps below to apply the change in the OCS portal. " +
          KAPTURE_UNAVAILABLE_MSG,
        kapture_steps: steps,
        kv_state: state ?? null,
        kv_warning: kvWarning,
        portal_base_url: portalBaseUrl,
      });
    },
  );

  // =========================================================================
  // greenzone_whitelist_remove
  // =========================================================================
  server.registerTool(
    "greenzone_whitelist_remove",
    {
      title: "Greenzone Whitelist — Remove",
      description:
        "Remove a hostname and/or IP address from the Bridge4IP Greenzone whitelist. " +
        "This operates via browser automation (Kapture) against the OCS portal. " +
        "Provide `host` and/or `ip` to remove — at least one required. " +
        "Returns Kapture step sequence for Claude Code to execute. " +
        "Requires 'write' scope and Kapture running in Sam's Chrome.",
      inputSchema: {
        host: z
          .string()
          .optional()
          .describe("Hostname to remove from whitelist"),
        ip: z
          .string()
          .optional()
          .describe("IP address or CIDR to remove from whitelist"),
        dry_run: z
          .boolean()
          .optional()
          .describe("If true, return the Kapture steps without executing portal automation"),
      },
      annotations: { destructiveHint: true },
    },
    async (args): Promise<ToolResult> => {
      if (!ctx.props.scope.includes("write")) {
        return err(
          `Scope denied: greenzone_whitelist_remove requires 'write' scope. Your token has: [${ctx.props.scope.join(", ")}].`,
        );
      }

      const { host, ip, dry_run } = args as { host?: string; ip?: string; dry_run?: boolean };

      if (!host && !ip) {
        return err("At least one of 'host' or 'ip' must be provided.");
      }

      const portalBaseUrl = resolvePortalBaseUrl(ctx.env);

      const steps = [
        ...(host ? buildRemoveHostSteps(portalBaseUrl, host) : []),
        ...(ip ? buildRemoveIpSteps(portalBaseUrl, ip) : []),
      ];

      if (dry_run) {
        return ok({
          dry_run: true,
          message: "Would execute the following Kapture steps to remove from OCS portal:",
          kapture_steps: steps,
          host,
          ip,
        });
      }

      const kv = getGreenzoneKv(ctx.env);
      let state: GreenzoneState | undefined;
      let kvWarning: string | undefined;

      if (kv) {
        try {
          state = await readState(kv);
          if (host) state = stateRemoveHost(state, host);
          if (ip) state = stateRemoveIp(state, ip);
          await writeState(kv, state);
        } catch (e) {
          kvWarning = `KV write failed: ${e instanceof Error ? e.message : String(e)}`;
        }
      } else {
        kvWarning = "GREENZONE_STATE_KV not bound in stdio mode — state not persisted.";
      }

      return ok({
        requires_kapture: true,
        message:
          "Execute the Kapture steps below to apply the removal in the OCS portal. " +
          KAPTURE_UNAVAILABLE_MSG,
        kapture_steps: steps,
        kv_state: state ?? null,
        kv_warning: kvWarning,
        portal_base_url: portalBaseUrl,
      });
    },
  );

  // =========================================================================
  // greenzone_whitelist_list
  // =========================================================================
  server.registerTool(
    "greenzone_whitelist_list",
    {
      title: "Greenzone Whitelist — List",
      description:
        "List current Greenzone whitelist entries from the KV cache. " +
        "Returns all whitelisted hosts and IPs, when the state was last synced, " +
        "and whether the last hourly reconciliation detected drift from the portal. " +
        "This is a fast read from KV — no browser automation required. " +
        "Use `from_portal: true` to trigger a live portal read via Kapture instead of KV. " +
        "Requires 'read' scope.",
      inputSchema: {
        from_portal: z
          .boolean()
          .optional()
          .describe(
            "When true, return Kapture steps to read live portal state instead of KV cache",
          ),
      },
      annotations: { readOnlyHint: true },
    },
    async (args): Promise<ToolResult> => {
      if (!ctx.props.scope.includes("read")) {
        return err(
          `Scope denied: greenzone_whitelist_list requires 'read' scope. Your token has: [${ctx.props.scope.join(", ")}].`,
        );
      }

      const { from_portal } = args as { from_portal?: boolean };

      if (from_portal) {
        const portalBaseUrl = resolvePortalBaseUrl(ctx.env);
        return ok({
          requires_kapture: true,
          message:
            "Execute the Kapture steps below to read live Greenzone whitelist state from the OCS portal.",
          kapture_steps: buildListSteps(portalBaseUrl),
          portal_base_url: portalBaseUrl,
        });
      }

      const kv = getGreenzoneKv(ctx.env);
      if (!kv) {
        return ok({
          source: "kv_cache",
          hosts: [],
          ips: [],
          last_synced_at: null,
          last_reconciled_at: null,
          reconcile_drift: false,
          warning:
            "GREENZONE_STATE_KV not bound in stdio mode. Use from_portal: true to read live portal state.",
        });
      }

      try {
        const state = await readState(kv);
        return ok({
          source: "kv_cache",
          hosts: state.hosts,
          ips: state.ips,
          last_synced_at: state.last_synced_at,
          last_reconciled_at: state.last_reconciled_at ?? null,
          reconcile_drift: state.reconcile_drift ?? false,
        });
      } catch (e) {
        return err(`Failed to read Greenzone state from KV: ${e instanceof Error ? e.message : String(e)}`);
      }
    },
  );
}
